import { getUserInfo } from '../utils/tokenManager';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_SALES_CONDITION_API_BASE_URL;
  if (envBase) return envBase;

  if (typeof window !== 'undefined') {
    const host = String(window.location?.hostname || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8081/api/v1';
    }
  }

  return 'https://inapi.tripbox.kr/api/v1';
};

const API_BASE_URL = resolveApiBaseUrl();

export const fetchNotifications = async ({ cursor = null, limit = 30 } = {}) => {
  const { companyNo } = getUserInfo() || {};
  const recipientCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(recipientCompanyNo) || recipientCompanyNo <= 0) {
    throw new Error('로그인 업체 정보를 확인할 수 없습니다.');
  }

  const params = new URLSearchParams();
  params.set('recipientCompanyNo', String(recipientCompanyNo));
  if (cursor !== null && cursor !== undefined && cursor !== '') params.set('cursor', String(cursor));
  params.set('limit', String(limit || 30));

  const response = await fetch(`${API_BASE_URL}/notifications?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `알림 조회 실패: ${response.status}`));
  }

  return {
    items: Array.isArray(data?.items) ? data.items : [],
    unreadCount: Number(data?.unreadCount || 0)
  };
};

export const fetchUnreadNotificationCount = async () => {
  const { companyNo } = getUserInfo() || {};
  const recipientCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(recipientCompanyNo) || recipientCompanyNo <= 0) {
    throw new Error('로그인 업체 정보를 확인할 수 없습니다.');
  }

  const params = new URLSearchParams();
  params.set('recipientCompanyNo', String(recipientCompanyNo));

  const response = await fetch(`${API_BASE_URL}/notifications/unread-count?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `미읽음 알림 조회 실패: ${response.status}`));
  }
  return Number(data?.unreadCount || 0);
};

export const markNotificationRead = async ({ notificationId }) => {
  const { companyNo } = getUserInfo() || {};
  const recipientCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(recipientCompanyNo) || recipientCompanyNo <= 0) {
    throw new Error('로그인 업체 정보를 확인할 수 없습니다.');
  }

  const response = await fetch(`${API_BASE_URL}/notifications/${encodeURIComponent(String(notificationId))}/read`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipientCompanyNo })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `알림 읽음 처리 실패: ${response.status}`));
  }
  return { notification: data?.notification || null, unreadCount: Number(data?.unreadCount || 0) };
};

export const markAllNotificationsRead = async () => {
  const { companyNo } = getUserInfo() || {};
  const recipientCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(recipientCompanyNo) || recipientCompanyNo <= 0) {
    throw new Error('로그인 업체 정보를 확인할 수 없습니다.');
  }

  const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipientCompanyNo })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `전체 읽음 처리 실패: ${response.status}`));
  }
  return { updatedCount: Number(data?.updatedCount || 0), unreadCount: Number(data?.unreadCount || 0) };
};

const parseItem = (item) => ({
  requestId: Number(item?.requestId || 0),
  requestCompanyNo: Number(item?.requestCompanyNo || 0),
  requestCompanyName: String(item?.requestCompanyName || ''),
  requestUserNo: Number(item?.requestUserNo || 0),
  requestUserName: String(item?.requestUserName || ''),
  productNo: Number(item?.productNo || 0),
  productName: String(item?.productName || ''),
  status: String(item?.status || ''),
  conditionPendingSide: String(item?.conditionPendingSide || ''),
  requestNote: String(item?.requestNote || ''),
  reviewNote: String(item?.reviewNote || ''),
  trpSignedByUserNo:
    item?.trpSignedByUserNo === null || item?.trpSignedByUserNo === undefined ? null : Number(item.trpSignedByUserNo || 0),
  trpSignedAt: item?.trpSignedAt ? String(item.trpSignedAt) : null,
  crsSignedByUserNo:
    item?.crsSignedByUserNo === null || item?.crsSignedByUserNo === undefined ? null : Number(item.crsSignedByUserNo || 0),
  crsSignedAt: item?.crsSignedAt ? String(item.crsSignedAt) : null,
  cancelStatus: String(item?.cancelStatus || 'NONE'),
  cancelRequestedByCompanyNo:
    item?.cancelRequestedByCompanyNo === null || item?.cancelRequestedByCompanyNo === undefined
      ? null
      : Number(item.cancelRequestedByCompanyNo || 0),
  cancelRequestedByUserNo:
    item?.cancelRequestedByUserNo === null || item?.cancelRequestedByUserNo === undefined
      ? null
      : Number(item.cancelRequestedByUserNo || 0),
  cancelReason: String(item?.cancelReason || ''),
  cancelReviewNote: String(item?.cancelReviewNote || ''),
  cancelRequestedAt: item?.cancelRequestedAt ? String(item.cancelRequestedAt) : null,
  cancelDecidedAt: item?.cancelDecidedAt ? String(item.cancelDecidedAt) : null,
  cancelApprovedByUserNo:
    item?.cancelApprovedByUserNo === null || item?.cancelApprovedByUserNo === undefined
      ? null
      : Number(item.cancelApprovedByUserNo || 0),
  cancelRejectedByUserNo:
    item?.cancelRejectedByUserNo === null || item?.cancelRejectedByUserNo === undefined
      ? null
      : Number(item.cancelRejectedByUserNo || 0),
  cancelWithdrawnAt: item?.cancelWithdrawnAt ? String(item.cancelWithdrawnAt) : null,
  requestedSalesCondition:
    item?.requestedSalesCondition && typeof item.requestedSalesCondition === 'object'
      ? item.requestedSalesCondition
      : {},
  proposedSalesCondition:
    item?.proposedSalesCondition && typeof item?.proposedSalesCondition === 'object'
      ? item.proposedSalesCondition
      : {},
  reviewedBySide: item?.reviewedBySide ? String(item.reviewedBySide) : null,
  approvedBySide: item?.approvedBySide ? String(item.approvedBySide) : null,
  rejectedBySide: item?.rejectedBySide ? String(item.rejectedBySide) : null,
  createdAt: item?.createdAt ? String(item.createdAt) : null,
  updatedAt: item?.updatedAt ? String(item.updatedAt) : null,
  reviewedAt: item?.reviewedAt ? String(item.reviewedAt) : null,
  approvedAt: item?.approvedAt ? String(item.approvedAt) : null,
  rejectedAt: item?.rejectedAt ? String(item.rejectedAt) : null,
});

export const fetchContractRequestList = async ({ status = '', requestCompanyNo = '', productNo = '' } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', String(status).toUpperCase());
  if (requestCompanyNo !== '' && requestCompanyNo !== null && requestCompanyNo !== undefined) {
    params.set('requestCompanyNo', String(requestCompanyNo));
  }
  if (productNo !== '' && productNo !== null && productNo !== undefined) {
    params.set('productNo', String(productNo));
  }
  params.set('limit', '300');

  const response = await fetch(`${API_BASE_URL}/sales-contract-requests?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`계약 요청 목록 조회 실패: ${response.status}`);
  }

  const data = await response.json();
  const list = Array.isArray(data?.items) ? data.items : [];
  return list.map(parseItem);
};

export const updateContractRequestStatus = async ({ requestId, action, reviewNote = '' }) => {
  const { userNo, companyNo } = getUserInfo() || {};
  const actorUserNo = Number(userNo || 0);
  const actorCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(actorUserNo) || actorUserNo <= 0) {
    throw new Error('로그인 사용자 정보를 확인할 수 없습니다.');
  }

  const response = await fetch(`${API_BASE_URL}/sales-contract-requests/${encodeURIComponent(String(requestId))}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: String(action || '').toUpperCase(),
      actorUserNo,
      actorCompanyNo: Number.isFinite(actorCompanyNo) && actorCompanyNo > 0 ? actorCompanyNo : null,
      reviewNote: String(reviewNote || '')
    })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `계약 요청 상태 변경 실패: ${response.status}`));
  }

  return parseItem(data?.request || {});
};

export const proposeContractRequestCondition = async ({ requestId, proposedSalesCondition, message = '' }) => {
  const { userNo, companyNo } = getUserInfo() || {};
  const actorUserNo = Number(userNo || 0);
  const actorCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(actorUserNo) || actorUserNo <= 0 || !Number.isFinite(actorCompanyNo) || actorCompanyNo <= 0) {
    throw new Error('로그인 사용자 정보를 확인할 수 없습니다.');
  }

  const response = await fetch(
    `${API_BASE_URL}/sales-contract-requests/${encodeURIComponent(String(requestId))}/conditions`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        actorCompanyNo,
        actorUserNo,
        proposedSalesCondition: proposedSalesCondition && typeof proposedSalesCondition === 'object' ? proposedSalesCondition : {},
        message: String(message || '')
      })
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `계약조건 제안 실패: ${response.status}`));
  }

  return parseItem(data?.request || {});
};

export const fetchContractRequestTimeline = async ({ requestId, limit = 300 }) => {
  const response = await fetch(
    `${API_BASE_URL}/sales-contract-requests/${encodeURIComponent(String(requestId))}/timeline?limit=${encodeURIComponent(String(limit))}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `타임라인 조회 실패: ${response.status}`));
  }
  const items = Array.isArray(data?.items) ? data.items : [];
  return { request: data?.request || null, items };
};

export const createContractRequestComment = async ({ requestId, message, scope = 'REQUEST' }) => {
  const { userNo, companyNo } = getUserInfo() || {};
  const actorUserNo = Number(userNo || 0);
  const actorCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(actorUserNo) || actorUserNo <= 0 || !Number.isFinite(actorCompanyNo) || actorCompanyNo <= 0) {
    throw new Error('로그인 사용자 정보를 확인할 수 없습니다.');
  }

  const response = await fetch(
    `${API_BASE_URL}/sales-contract-requests/${encodeURIComponent(String(requestId))}/comments`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        actorCompanyNo,
        actorUserNo,
        scope: String(scope || 'REQUEST'),
        message: String(message || '')
      })
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `코멘트 등록 실패: ${response.status}`));
  }

  return data?.comment || null;
};

export const signContractRequest = async ({ requestId }) => {
  const { userNo, companyNo } = getUserInfo() || {};
  const actorUserNo = Number(userNo || 0);
  const actorCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(actorUserNo) || actorUserNo <= 0 || !Number.isFinite(actorCompanyNo) || actorCompanyNo <= 0) {
    throw new Error('로그인 사용자 정보를 확인할 수 없습니다.');
  }

  const response = await fetch(
    `${API_BASE_URL}/sales-contract-requests/${encodeURIComponent(String(requestId))}/signatures`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actorCompanyNo, actorUserNo })
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `서명 처리 실패: ${response.status}`));
  }

  return parseItem(data?.request || {});
};

export const requestContractCancel = async ({ requestId, reason }) => {
  const { userNo, companyNo } = getUserInfo() || {};
  const actorUserNo = Number(userNo || 0);
  const actorCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(actorUserNo) || actorUserNo <= 0 || !Number.isFinite(actorCompanyNo) || actorCompanyNo <= 0) {
    throw new Error('로그인 사용자 정보를 확인할 수 없습니다.');
  }

  const response = await fetch(
    `${API_BASE_URL}/sales-contract-requests/${encodeURIComponent(String(requestId))}/cancel-requests`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        actorCompanyNo,
        actorUserNo,
        reason: String(reason || '')
      })
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `취소 요청 실패: ${response.status}`));
  }

  return parseItem(data?.request || {});
};

export const updateContractCancelStatus = async ({ requestId, action, reviewNote = '' }) => {
  const { userNo, companyNo } = getUserInfo() || {};
  const actorUserNo = Number(userNo || 0);
  const actorCompanyNo = Number(companyNo || 0);
  if (!Number.isFinite(actorUserNo) || actorUserNo <= 0 || !Number.isFinite(actorCompanyNo) || actorCompanyNo <= 0) {
    throw new Error('로그인 사용자 정보를 확인할 수 없습니다.');
  }

  const response = await fetch(
    `${API_BASE_URL}/sales-contract-requests/${encodeURIComponent(String(requestId))}/cancel-status`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: String(action || '').toUpperCase(),
        actorCompanyNo,
        actorUserNo,
        reviewNote: String(reviewNote || '')
      })
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(data?.message || `취소 상태 변경 실패: ${response.status}`));
  }

  return parseItem(data?.request || {});
};
